/**
 * Pure geometry for the zoomable image viewer — no DOM, so it unit-tests under
 * plain `node --test`.
 *
 * Zoom is relative to the "fit" size (the whole image visible, aspect kept):
 * `1` is fit, `MAX_ZOOM` is four times that.
 */

export const MIN_ZOOM = 1
export const MAX_ZOOM = 4
/** Multiplier for one zoom-in / zoom-out button press. */
export const ZOOM_STEP = 1.5
/** Where a double-click / double-tap jumps to from fit. */
export const DOUBLE_TAP_ZOOM = 2.5

export interface Size {
  width: number
  height: number
}

export function clampZoom(zoom: number): number {
  if (!Number.isFinite(zoom)) return MIN_ZOOM
  return Math.min(MAX_ZOOM, Math.max(MIN_ZOOM, zoom))
}

/**
 * One button press from `zoom`, in `direction` (1 in, -1 out). Snaps to fit
 * when a zoom-out lands close to it, so the diner never sits at 1.02×.
 */
export function stepZoom(zoom: number, direction: 1 | -1): number {
  const next = clampZoom(direction === 1 ? zoom * ZOOM_STEP : zoom / ZOOM_STEP)
  return next < MIN_ZOOM * 1.1 ? MIN_ZOOM : next
}

/** Double-click / double-tap: zoom in from fit, back to fit from anywhere else. */
export function toggleZoom(zoom: number): number {
  return zoom > MIN_ZOOM ? MIN_ZOOM : DOUBLE_TAP_ZOOM
}

/**
 * The largest size of `natural` that fits inside `box` with its aspect ratio
 * kept — never upscaled past the image's own pixels. Zero-sized input (an image
 * not loaded yet, a hidden box) yields zero.
 */
export function fitSize(natural: Size, box: Size): Size {
  if (
    natural.width <= 0 ||
    natural.height <= 0 ||
    box.width <= 0 ||
    box.height <= 0
  ) {
    return { width: 0, height: 0 }
  }
  const scale = Math.min(
    box.width / natural.width,
    box.height / natural.height,
    1
  )
  return { width: natural.width * scale, height: natural.height * scale }
}

/**
 * Scroll offset along one axis that keeps the content point under `pointer`
 * (px from the viewport's start edge) in place when content grows from
 * `fromExtent` to `toExtent`. Clamped to the scrollable range.
 */
export function anchoredScroll({
  scroll,
  pointer,
  fromExtent,
  toExtent,
  viewport,
}: {
  scroll: number
  pointer: number
  fromExtent: number
  toExtent: number
  viewport: number
}): number {
  if (fromExtent <= 0) return 0
  const ratio = (scroll + pointer) / fromExtent
  const max = Math.max(0, toExtent - viewport)
  return Math.min(max, Math.max(0, ratio * toExtent - pointer))
}

/** Distance between two pointers — the pinch gesture's measure. */
export function pointerDistance(
  a: { x: number; y: number },
  b: { x: number; y: number }
): number {
  return Math.hypot(a.x - b.x, a.y - b.y)
}
